/**
 * Frontend mirror of backend getRequiredSsoFields.
 * Keep in sync with TurboIAMBackend/src/services/getRequiredSsoFields.ts
 */

export type SsoFieldRequirementInput = {
  sso_type?: string | null;
  grant_type?: string | null;
  entity_id?: string | null;
  acs_url?: string | null;
  metadata_url?: string | null;
  metadata_xml?: string | null;
  redirect_uris?: string[] | null;
  oidc_discovery_endpoint?: string | null;
  /** Application (client) ID — required for Client Credentials unless create-on-approve. Never a secret. */
  client_id?: string | null;
  /** When true, SSO team creates Entra App Registration after approval — client_id not required at submit. */
  request_entra_create_on_approve?: boolean | null;
};

export type SsoFieldRequirements = {
  protocol: "SAML" | "OIDC" | null;
  grantType: string | null;
  required: string[];
  missingLabels: string[];
  ready: boolean;
};

function hasText(v?: string | null): boolean {
  return Boolean(v && String(v).trim());
}

function hasUris(uris?: string[] | null): boolean {
  return Array.isArray(uris) && uris.some((u) => String(u || "").trim());
}

export function getRequiredSsoFields(
  input: SsoFieldRequirementInput,
): SsoFieldRequirements {
  const t = String(input.sso_type || "")
    .trim()
    .toUpperCase();
  const protocol = t === "SAML" ? "SAML" : t === "OIDC" || t === "OPENID" || t === "OPENID_CONNECT" ? "OIDC" : null;
  const grantType = String(input.grant_type || "")
    .trim()
    .toLowerCase() || null;

  if (!protocol) {
    return {
      protocol: null,
      grantType,
      required: [],
      missingLabels: ["protocol (SAML or OIDC)"],
      ready: false,
    };
  }

  if (protocol === "SAML") {
    const hasMeta =
      hasText(input.metadata_url) ||
      String(input.metadata_xml || "").trim().length > 40;
    const hasSp = hasText(input.entity_id) && hasText(input.acs_url);
    const ready = hasSp || hasMeta;
    const missingLabels: string[] = [];
    if (!ready) {
      missingLabels.push("Entity ID", "ACS URL");
    }
    return {
      protocol,
      grantType,
      required: ["entityId", "acsUrl"],
      missingLabels,
      ready,
    };
  }

  // OIDC Client Credentials — M2M; no redirect / ACS / Entity ID.
  // Client ID required unless SSO team will create Entra on approve.
  if (grantType === "client_credentials") {
    const requestCreate = !!input.request_entra_create_on_approve;
    const hasClientId = hasText(input.client_id);
    return {
      protocol,
      grantType,
      required: requestCreate ? [] : ["clientId"],
      missingLabels: requestCreate || hasClientId ? [] : ["Application (client) ID"],
      ready: requestCreate || hasClientId,
    };
  }

  const hasRedirect = hasUris(input.redirect_uris);
  return {
    protocol,
    grantType,
    required: ["redirectUri"],
    missingLabels: hasRedirect ? [] : ["redirect URI"],
    ready: hasRedirect,
  };
}
